import * as React from 'react';
import { connect } from 'react-redux';

import AddStorySnippetButton from '../../add-story-snippet-button/AddStorySnippetButton';
import StorySnippetCard from '../../story-snippet-card/StorySnippetCard';

import { showSnackBarErrors } from '../../../redux/actions';

import { __t, applyTranslations } from 'utils/language';
import { getStorySnippetValidationErrors } from '../../../utils/snippets';

import './widget.scss';

import {
  State
} from 'types';
import { DeliverableStoryDetails } from '../../../types/general';
import {
  StorySnippetValidationErrorMap
} from '../../../types/validation';

interface OwnProps {
  deliverableId: number,
  orgType: 'brand' | 'influencer'
}

interface StateProps {
  snippets: Pick<DeliverableStoryDetails, 'snippets'>['snippets'],
  lastValidateAt: null | number
}

interface DispatchProps {
  dispatchShowSnackBarErrors: (e: StorySnippetValidationErrorMap) => void
}

type Props = OwnProps & StateProps & DispatchProps;

applyTranslations(require('./widget.i18n.json'));

const Widget = ({
  orgType,
  snippets,
  deliverableId,
  lastValidateAt,
  dispatchShowSnackBarErrors
}: Props) => {
  const errorsMap = snippets.reduce((acc, snip) => {
    acc[snip.id] = lastValidateAt !== null ? getStorySnippetValidationErrors(snip) : [];

    return acc;
  }, {} as StorySnippetValidationErrorMap);

  React.useEffect(() => {
    if (lastValidateAt === null) {
      return;
    }

    dispatchShowSnackBarErrors(errorsMap);
  }, [lastValidateAt]);

  return (
    <div className="story-snippets-widget">
      <div className="story-snippets-widget__header story-snippets-widget__header--under">
        {orgType === 'brand' ? (
          <React.Fragment>
            {__t('story_snippets_widget.story_brand.header')}
            {snippets.length === 0 ? (
              <span className="story-snippets-widget__header-description">
                {__t('story_snippets_widget.story_brand.empty_description')}
              </span>
            ) : (
              <span className="story-snippets-widget__header-description">
                {__t('story_snippets_widget.story_brand.description')}
              </span>
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {__t('story_snippets_widget.story_influencer.header')}
            <span className="story-snippets-widget__header-description story-snippets-widget__header-description--list">
              <span>
                {__t('story_snippets_widget.story_influencer.description1')}
              </span>
              <span>
                {__t('story_snippets_widget.story_influencer.description2')}
              </span>
            </span>
          </React.Fragment>
        )}
      </div>
      <div className="story-snippets-widget__add-button">
        <AddStorySnippetButton
          deliverableId={deliverableId}
        />
      </div>
      <div className="story-snippets-widget__cards">
        {snippets.map(snippet => (
          <StorySnippetCard
            key={snippet.id}
            index={snippet.id + 1}
            snippetId={snippet.id}
            story={snippet.story}
            insights={snippet.insights}
            deliverableId={deliverableId}
            errors={errorsMap[snippet.id]}
          />
        ))}
      </div>
    </div>
  );
};

const mapStateToProps = ({ storySnippets }: State, { deliverableId }: OwnProps): StateProps => ({
  snippets: storySnippets.deliverables[deliverableId]?.snippets ?? [],
  lastValidateAt: storySnippets.deliverables[deliverableId]?.lastValidateAt ?? null
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchShowSnackBarErrors: errors => dispatch(showSnackBarErrors(errors))
});

export default connect(mapStateToProps, mapDispatchToProps)(Widget);
